import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { Order } from '../../interfaces/orders/order';
import { OrderChange } from '../../interfaces/order-change';
import { eOrderChangeOperation } from '../../enums';
import { OrderManagerService } from '../../services/order-manager/order-manager.service';
import { BehaviorSubject, filter, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { OrderRepositoryService } from '../../services/order/order-repository-service.service';
import { PathConversionService } from '../../services/path-conversion/path-conversion.service';
import { OrderUIConfiguration } from '../../interfaces/ui-configurations/order-ui-configuration';
import { OrderAction } from '../../interfaces/orderAction';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { OrderChangesService } from '../../services/order-changes/order-changes.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'lib-order-changes',
    templateUrl: './order-changes.component.html',
    styleUrls: ['./order-changes.component.css'],
    animations: [
        trigger('slideDown', [
            state('hidden', style({
                opacity: 0
            })),
            state('visible', style({
                opacity: 1
            })),
            transition('hidden <=> visible', [
                animate('0.5s cubic-bezier(0.5, 0.1, 0.1, 1)')
            ])
        ]),
        trigger('rotate', [
            state('unrotated', style({
                rotate: '0deg'
            })),
            state('rotated', style({
                rotate: '-180deg'
            })),
            transition('unrotated <=> rotated', [
                animate('0.5s cubic-bezier(0.5, 0.1, 0.1, 1)')
            ])
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OrderChangesComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  @Input() saving$!: Observable<boolean>;
  @Input() uiConfiguration!: OrderUIConfiguration;
  @Input() isAdminPortal = false;

  protected orderChanges$ = new BehaviorSubject<OrderChange[]>([]);
  protected orderActions$ = new BehaviorSubject<OrderAction[]>([]);

  private onDestroy$ = new Subject<void>();

  protected originalOrder!: Order;
  protected formText: string | undefined;

  protected undoHoverStates: string[] = [];

  constructor(
    private orderManager: OrderManagerService,
    private ordersService: OrderRepositoryService,
    private pathConversionService: PathConversionService,
    private orderChangesService: OrderChangesService,
    private dialog: MatDialog) { }

  ngOnInit(): void {
    this.orderManager.currentOrder.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(
      order => {
        this.orderChanges$.next(order.orderChanges ?? []);
        this.undoHoverStates = order.orderChanges?.map(c => 'unrotated') ?? [];
      });

    this.orderManager.currentOrder.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(
      order => this.formText = [... new Set(order.orderActions?.map(action => action.registryForm?.formFull))].join(', '));

    this.orderManager.parentOrder.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(order => this.originalOrder = order);

    if (!this.orderManager.parentOrder.value.id) {
      const orderChangesSubscription = this.orderChanges$.pipe(
        takeUntil(this.onDestroy$),
        filter(changes => changes.length > 0),
        switchMap(changes => this.ordersService.getOrder('Orders', changes.at(0)!.originalOrderID))
      ).subscribe(order => {
        this.orderManager.updateParentOrder(order);
        orderChangesSubscription.unsubscribe();
      });
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  protected getIcon(change: OrderChange): string {
    if (change.operation === eOrderChangeOperation.Add) {
      return 'add_circle_outline';
    } else if (change.operation === eOrderChangeOperation.Remove) {
      return 'remove_circle_outline';
    } else if (change.operation === eOrderChangeOperation.Replace) {
      return 'swap_horiz';
    } else {
      return '';
    }
  }

  protected getIconTooltip(change: OrderChange): string {
    if (change.operation === eOrderChangeOperation.Add) {
      return this.transloco.translate('common.labels.added');
    } else if (change.operation === eOrderChangeOperation.Remove) {
      return this.transloco.translate('orderChanges.removed');
    } else if (change.operation === eOrderChangeOperation.Replace) {
      return this.transloco.translate('orderChanges.replaced');
    } else {
      return '';
    }
  }

  protected convertPath(change: OrderChange): string {
    let formattedPath: string = change.path;
    //matches 'Parties', 'Parties/' or 'Parties/{some guid}'
    const partiesRegex = /^Parties(?:\/(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})?)?$/
    const assetsRegex = /^Assets(?:\/(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})?)?$/

    if (partiesRegex.test(change.path)) {
      if ((change.newValue?.includes('Debtor') ?? false) || (change.originalValue?.includes('Debtor') ?? false)) {
        formattedPath = this.transloco.translate('common.labels.debtors');
      } else if ((change.newValue?.includes('Dealer') ?? false) || (change.originalValue?.includes('Dealer') ?? false)) {
        formattedPath = this.transloco.translate('common.labels.dealers');
      } else if ((change.newValue?.includes('Secured') ?? false) || (change.originalValue?.includes('Secured') ?? false)) {
        formattedPath = this.transloco.translate('common.labels.securedParties');
      } else if ((change.newValue?.includes('Registering') ?? false) || (change.originalValue?.includes('Registering') ?? false)) {
        formattedPath = this.transloco.translate('common.labels.registeringAgents');
      }
    } else if (assetsRegex.test(change.path)) {
      return this.transloco.translate('common.labels.assets');
    } else if (change.path.toLowerCase().endsWith('collateraltoadd')) {
      return this.transloco.translate('orderChanges.generalCollateralToAdd');
    } else if (change.path.toLowerCase().endsWith('collateraltoremove')) {
      return this.transloco.translate('orderChanges.generalCollateralToRemove');
    } else if (this.originalOrder) {
      formattedPath = this.pathConversionService.convertPath(this.originalOrder, change.path, true);
    } else {
      formattedPath = '';
    }

    return formattedPath;
  }

  protected formatDescription(change: OrderChange): string {
    if (change.operation === eOrderChangeOperation.Add) {
      return this.transloco.translate('orderChanges.addedDescription', { value: change.newValue });
    } else if (change.operation === eOrderChangeOperation.Remove) {
      return this.transloco.translate('orderChanges.removedDescription', { value: change.originalValue })
    } else if (change.operation === eOrderChangeOperation.Replace) {
      if (change.newValue && !change.originalValue) {
        return this.transloco.translate('orderChanges.replacedEmptyDescription', { newValue: this.formatValue(change.formattedNewValue) })
      } else if (!change.newValue && change.originalValue) {
        return this.transloco.translate('orderChanges.replacedWithEmptyDescription', { originalValue: this.formatValue(change.formattedOriginalValue) })
      } else {
        return this.transloco.translate('orderChanges.replacedDescription', { originalValue: this.formatValue(change.formattedOriginalValue), newValue: this.formatValue(change.formattedNewValue) })
      }
    } else {
      return '';
    }
  }

  //Todo: move into API
  protected formatValue(value: string | undefined): string {
    const dateRegex = /^(?:\d{4}[-\/]\d{2}[-\/]\d{2}|\d{2}[-\/]\d{2}[-\/]\d{4})(?:\s(?:\d{1,2}:\d{2}(?::\d{2})?\s?(?:AM|PM|am|pm)?))?$/;
    if (value?.toLowerCase() === 'true') {
      return 'checked';
    } else if (value?.toLowerCase() === 'false') {
      return 'unchecked';
    } else if (dateRegex.test(value ?? '')) {
      return value?.split(' ')?.at(0) ?? value ?? '';
    } else {
      return value ?? '';
    }
  }

  protected onUndoMouseEnter(change: OrderChange) {
    this.undoHoverStates[this.getChangeIndex(change)] = 'rotated';
  }

  protected onUndoMouseLeave(change: OrderChange) {
    this.undoHoverStates[this.getChangeIndex(change)] = 'unrotated'
  }

  protected getChangeIndex(change: OrderChange) {
    const index = this.orderChanges$.value.findIndex(c => c.id === change.id);
    return index;
  }

  protected undoChange(change: OrderChange) {
    if (this.isAdminPortal) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: this.transloco.translate('common.labels.areYouSure'),
          confirmationPrompt: this.transloco.translate('orderChanges.undoChangePrompt'),
          positiveResponseLabel: this.transloco.translate('common.buttons.yes'),
          negativeResponseLabel: this.transloco.translate('common.buttons.cancel')
        },
        width: '500px'
      });

      dialogRef.afterClosed().subscribe(res => {
        if (res) {
          this.orderChangesService.undoChange(this.orderManager.currentOrder.value.id, change.id)
            .subscribe(order => {
              this.orderManager.currentOrder.next(order.resource);
            });
        }
      });
    } else {
      this.orderChangesService.undoChange(this.orderManager.currentOrder.value.id, change.id)
        .subscribe(order => {
          this.orderManager.currentOrder.next(order.resource);
        });
    }
  }
}
